import { useSnackbar } from 'notistack';
import { useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';

import { useBoolean } from 'src/hooks/use-boolean';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { DocumentType } from 'src/services/files/file.types';
import candidate_scan from 'src/assets/lottie/candidate.json';
import { generateResumeImportError, generateUploadError } from 'src/services/files/file.utils';
import { useImportResumeMutation, useImportUnlinkedCandidateMutation } from 'src/services/candidates/candidates.service';
import {
  useUploadFileMutation,
  useCreateDocumentMutation,
} from 'src/services/files/file.service';

import { Upload } from 'src/components/upload';
import { useSettingsContext } from 'src/components/settings';
import AnimationContainer from 'src/components/animation/container';
import UploadItemCard from 'src/components/upload/upload-item-card';
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { generateCandidateImportErrorMessage } from 'src/services/candidates/candidates.utils';

type Props = {
  open: boolean;
  onClose: VoidFunction;
};

export default function CandidateImporter({ open, onClose }: Props) {
  const { t } = useTranslate();

  const settings = useSettingsContext();

  const { enqueueSnackbar } = useSnackbar();

  const authContext = useAuthContext();

  const [uploadFile, { isLoading: uploadingFile }] = useUploadFileMutation();

  const [createDocument, { isLoading: creatingDocument }] = useCreateDocumentMutation();

  const [importCandidate, { isLoading: importingCandidate }] = useImportUnlinkedCandidateMutation();

  const [file, setFile] = useState<File>();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleDocumentDrop = useCallback(
    async (documents: File[]) => {
      setFile(documents[0]);
    },
    [setFile]
  );

  const handleClose = () => {
    setFile(undefined);
    onClose();
  }

  const handleDocumentProcess = useCallback(async () => {

    try {
      const uploadedFile = await uploadFile({
        file: file as File,
        type: 'document' as any,
      }).unwrap();

      const document = await createDocument({
        organization_id: authContext.organization.id,
        document: {
          file_id: uploadedFile.id,
          name: (file as File).name,
          type: DocumentType.RESUME,
        },
      }).unwrap();

      await importCandidate({
        data: {
          document_id: document.id as string
        }
      }).unwrap();

      enqueueSnackbar(t('candidates.api.import_candidate.success'), { variant: 'success' });
      handleClose();
    } catch (e) {
      enqueueSnackbar(generateResumeImportError(e), { variant: 'error' });
      setFile(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    authContext.organization.id,
    createDocument,
    enqueueSnackbar,
    file,
    importCandidate,
    handleClose,
    uploadFile,
  ]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <DialogTitle>{t('candidates.importer.title')}</DialogTitle>
      <DialogContent>
        <Stack gap={2} width="100%">

          {errorMessage && (
            <Alert sx={{ mb: 2 }} severity="error">
              {errorMessage}
            </Alert>
          )}

          <Box width="100%" maxHeight='80vh' sx={{ mb: 2 }}>
            {!importingCandidate ? (
              <Upload
                accept={{ '.pdf': [], '.docx': [], '.doc': [] }}
                onDrop={handleDocumentDrop}
                sx={{ mt: 1 }}
                helperText={t('components.resume_importer.helper_text')}
              />
            ) : (
              <Box display="flex" flexDirection="column" justifyContent="center" textAlign="center" alignItems="center">
                <AnimationContainer height={320} width={410} json={candidate_scan} loop />
                <Typography
                  fontSize={13}
                  className={settings.themeMode === 'light' ? 'text-gray-600' : 'text-gray-100'}
                >
                  {t('candidates.importer.hold_on')}
                </Typography>
              </Box>
            )}
          </Box>

          {file && (
            <UploadItemCard
              title={(file as any).path}
              onDeleteFile={() => setFile(undefined)}
              uploading={uploadingFile}
              disabled={uploadingFile || importingCandidate}
            />
          )}

          <LoadingButton
            disabled={!file}
            variant="contained"
            fullWidth
            loading={creatingDocument || uploadingFile || importingCandidate}
            onClick={handleDocumentProcess}
          >
            {t('common.upload')}
          </LoadingButton>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('common.cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
}
